import useHorizontalGrabScroll from '@utils/hooks/useHorizontalGrabScroll';
import useHorizontalScrollShadows from '@utils/hooks/useHorizontalScrollShadows';
import useTabInteractionCheck from '@utils/hooks/useTabInteractionCheck';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
  PropsWithChildren,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

export interface IShadowContainerHorizontalRef {
  readonly scrollTo: (offset: number) => void;
  readonly getOffsetWidth: () => number;
}

interface Props {
  readonly className?: string;
  readonly innerWrapperClassName?: string;
  readonly shadowColour?: string;
  readonly threshold?: number;
  readonly showStart?: boolean; // Manually override shadows from a parent component
  readonly showEnd?: boolean;
  readonly disableScroll?: boolean; // Used together with showStart/showEnd to disable scrolling within the container (e.g. when we want to wrap a carousel within some left/right shadows, we want to use the carousel's scrolling/swiping, not the container's one)
  readonly shadowWidth?: string;
  readonly isNoWrapEnabled?: boolean;
  readonly hasTabbableNestedItems?: boolean;
}

const ShadowContainerHorizontal = forwardRef<
  IShadowContainerHorizontalRef,
  PropsWithChildren<Props>
>(
  (
    {
      children,
      className,
      innerWrapperClassName,
      disableScroll = false,
      shadowColour = 'from-white dark:from-black-100',
      threshold = 0.05,
      showStart = false,
      showEnd = true,
      shadowWidth = 'w-10',
      isNoWrapEnabled = true,
      hasTabbableNestedItems,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isTabInteraction = useTabInteractionCheck();
    const { isStart, isEnd } = useHorizontalScrollShadows(
      containerRef,
      threshold,
      disableScroll
    );
    useHorizontalGrabScroll(containerRef, disableScroll);

    useImperativeHandle(ref, () => ({
      scrollTo: (leftOffset: number) => {
        containerRef.current?.scrollTo({
          left: leftOffset,
          behavior: 'smooth',
        });
      },
      getOffsetWidth: () => containerRef.current?.offsetWidth ?? 0,
    }));

    useEffect(() => {
      if (
        !containerRef?.current ||
        !isTabInteraction ||
        !hasTabbableNestedItems
      )
        return () => ({});

      const nestedItemsList =
        containerRef.current.firstElementChild?.children || [];
      const nestedItemsListLength = nestedItemsList?.length;
      if (nestedItemsListLength === 0) return () => ({});

      const focusInListener = (left: number) => () => {
        containerRef.current?.scrollTo({
          left,
        });
      };
      const firstItemListener = focusInListener(0);
      const lastItemListener = focusInListener(
        containerRef.current.scrollWidth
      );

      const isElementTabbable = (element: Element) => {
        const querySelector = 'a[href],button:not([disabled])';

        return (
          element.matches(querySelector) ||
          !!element.querySelector(querySelector)
        );
      };

      // It could be that we have a list of items where some items could be disabled or
      // not tabbable, so we need to compute the first and last tabbable elements
      const firstTabbableItem = Array.from(nestedItemsList).find((element) =>
        isElementTabbable(element)
      );
      const lastTabbableItem = Array.from(nestedItemsList).findLast((element) =>
        isElementTabbable(element)
      );

      firstTabbableItem?.addEventListener('focusin', firstItemListener);
      lastTabbableItem?.addEventListener('focusin', lastItemListener);

      return () => {
        firstTabbableItem?.removeEventListener('focusin', firstItemListener);
        lastTabbableItem?.removeEventListener('focusin', lastItemListener);
      };
    }, [isTabInteraction, hasTabbableNestedItems]);

    return (
      <div className={classNames(className, 'relative')}>
        <AnimatePresence>
          {(!isStart || showStart) && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour,
                shadowWidth,
                '-ml-[1px] pointer-events-none absolute top-0 left-0 z-10 h-full bg-gradient-to-r to-transparent'
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>

        <div
          ref={containerRef}
          tabIndex={(isStart && isEnd) || disableScroll ? -1 : 0}
          className={classNames(
            {
              'z-0 overflow-x-hidden': disableScroll,
              'overflow-x-auto': !disableScroll,
              'whitespace-nowrap': isNoWrapEnabled,
            },
            'scrollbar-hide relative select-none',
            innerWrapperClassName
          )}
        >
          {children}
        </div>

        {/* isEnd is always false when using showEnd override so !isEnd is always true */}
        <AnimatePresence>
          {!isEnd && showEnd && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour || 'from-white dark:from-black-100',
                shadowWidth,
                '-ml-[1px] pointer-events-none absolute top-0 right-0 z-10 h-full bg-gradient-to-l to-transparent'
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default ShadowContainerHorizontal;
